import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-custom.css";
import { API_BASE_URL } from "../../utils/constants";
import { Button, Modal, Pagination } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import LoadingSpinner from "../../components/LoadingSpinner";
import AddDetailsModal from "../../components/AddDetailsModal";
import GlobalVideoUploadModal from "../../components/GlobalVideoUploadModal";
import VirtualMeetingModal from "../VirtualMeetingModal";
import { useHearingSession } from "../../hooks/useHearingSession";
import HearingLotSelect from "../../components/HearingLotSelect";

// ── Fetch cases for first hearing ─────────────────────────────────────────────

const fetchFirstHearingCases = async (formattedDate, arbId) => {
  const response = await fetch(`${API_BASE_URL}/api/GetOppforSecondHearing`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ Hearing_date: formattedDate, Arb_id: arbId }),
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const result = await response.json();
  const parsed = Array.isArray(result) ? result : JSON.parse(result);
  return parsed.map((item) => {
    const {
      Acc_id, Arb_Assign_id, Acc_date, Hearing_date, Video_link, Link_id,
      Password, No_of_cases, Rate, Hearing_Time_from, Hearing_Time_to, ...rest
    } = item;
    return { Case_id: item.Case_id || item.caseId, ...rest };
  });
};

// ── Column config ─────────────────────────────────────────────────────────────

const displayColumns = [
  { key: "Case_id",                 label: "Case ID" },
  { key: "referenceNo",             label: "Reference No" },
  { key: "accNo",                   label: "Account No" },
  { key: "Cust_name",               label: "Customer Name" },
  { key: "lotNo",                   label: "Lot No" },
  { key: "firstHearingDate",        label: "Hearing Date",       date: true },
  { key: "assignDate",              label: "Assign Date",         date: true },
  { key: "appointmentLetterDate",   label: "Appt Letter Date",    date: true },
  { key: "refDate",                 label: "Ref Date",            date: true },
  { key: "accDate",                 label: "Acc Date",            date: true },
  { key: "socDate",                 label: "SOC Date",            date: true },
  { key: "sec17AppDate",            label: "Sec17 App Date",      date: true },
  { key: "sec17OrderDate",          label: "Sec17 Order Date",    date: true },
  { key: "sec17OrderCertifiedDate", label: "Sec17 Certified",     date: true },
];

const fmtDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleDateString("en-IN");
};

// ─────────────────────────────────────────────────────────────────────────────

const FirstHearing = () => {
  const [showSecondModal, setShowSecondModal] = useState(false);

  const {
    arbId,
    selectedDate, formattedDate,
    availableDates, availableDateObjects, openToDate,
    data, filteredData, loading,
    currentItems, currentPage, setCurrentPage, totalPages, paginationPages,
    show, showVideoModal, setShowVideoModal,
    globalVideos, globalVideoInputRef, videosUploaded,
    lots, selectedLotKey, handleLotChange,
    handleDateChange, handleAppointments, clearData,
    handleAddDetails, handleClose, handleSaveDetails,
    handleGlobalVideoChange, handleRemoveGlobalVideo,
    handleCloseVideoModal, handleGlobalVideoUpload,
  } = useHearingSession({
    meetingNo: 1,
    availableDatesEndpoint: "AvailableFirstHearingDates",
    fetchCases: fetchFirstHearingCases,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container mt-2">
      <h4>Virtual Meeting For First Hearing</h4>

      <div className="row align-items-center my-2">
        <div className="col-md-2">
          <label htmlFor="datePicker" className="form-label">Select Date :-</label>
        </div>
        <div className="col-md-2">
          <DatePicker
            id="datePicker"
            selected={selectedDate}
            onChange={handleDateChange}
            includeDates={availableDateObjects}
            openToDate={openToDate}
            placeholderText="Select a date"
            dateFormat="MM/dd/yyyy"
            className="form-control date-picker-sm"
            disabled={availableDates.length === 0}
          />
        </div>
        {availableDates.length === 0 && (
          <div className="col-md-4">
            <small className="text-muted">No first hearing dates found</small>
          </div>
        )}
        {availableDates.length > 0 && (
          <div className="col-md-2">
            <button className="custBtn" onClick={handleAppointments}>Appointments</button>
          </div>
        )}
        <HearingLotSelect lots={lots} selectedLotKey={selectedLotKey} onChange={handleLotChange} />
        <div className="col-md-3">
          {filteredData.length > 0 && !videosUploaded && (
            <button className="custBtn" onClick={() => setShowVideoModal(true)}>
              Upload Videos
            </button>
          )}
          {filteredData.length > 0 && videosUploaded && (
            <button
              className="custBtn"
              onClick={() => setShowSecondModal(true)}
              style={{ whiteSpace: "nowrap" }}
            >
              Assign Second Hearing
            </button>
          )}
        </div>
      </div>

      {/* Cases Table */}
      <div className="row">
        <div className="col-md-12">
          <div className="table-responsive">
            {currentItems.length > 0 && (
              <table
                className="table table-striped table-bordered table-hover table-sm mt-3 text-center"
                style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <thead>
                  <tr>
                    {videosUploaded && <th>Action</th>}
                    {displayColumns.map((col) => <th key={col.key}>{col.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index}>
                      {videosUploaded && (
                        <td>
                          {item.detailsSaved
                            ? <span className="text-success fw-semibold">✓ Saved</span>
                            : <button className="custBtn" onClick={() => handleAddDetails(item.Case_id)}>Assign to Contested</button>
                          }
                        </td>
                      )}
                      {displayColumns.map((col) => (
                        <td key={col.key}>{col.date ? fmtDate(item[col.key]) : item[col.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} />
              {paginationPages.map(({ n, active }) => (
                <Pagination.Item key={n} active={active} onClick={() => setCurrentPage(n)}>{n}</Pagination.Item>
              ))}
              <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} />
            </Pagination>
          )}
        </div>
      </div>

      {/* ── Add Details Modal ──────────────────────────────────────────────────── */}
      <AddDetailsModal show={show} onHide={handleClose} onSave={handleSaveDetails} />

      {/* ── Upload Videos Modal ───────────────────────────────────────────────── */}
      <GlobalVideoUploadModal
        show={showVideoModal}
        onHide={handleCloseVideoModal}
        videoInputRef={globalVideoInputRef}
        videos={globalVideos}
        onVideoAdd={handleGlobalVideoChange}
        onVideoRemove={handleRemoveGlobalVideo}
        onUpload={handleGlobalVideoUpload}
        formattedDate={formattedDate}
        hearingLabel="first"
      />

      {/* ── Assign Second Hearing Modal ────────────────────────────────────────── */}
      <Modal show={showSecondModal} onHide={() => setShowSecondModal(false)} className="modal-xl">
        <Modal.Header className="customModal" style={{ position: "relative" }}>
          <Modal.Title>Assign Second Hearing Date</Modal.Title>
          <Button
            style={{ position: "absolute", right: "15px", color: "orange", backgroundColor: "transparent", border: "none", fontSize: "40px" }}
            onClick={() => setShowSecondModal(false)}
          >
            &times;
          </Button>
        </Modal.Header>
        <Modal.Body>
          <VirtualMeetingModal
            appointments={data}
            arbId={arbId}
            setShowSecondModal={setShowSecondModal}
            onSuccess={() => {
              setShowSecondModal(false);
              clearData();
            }}
          />
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default FirstHearing;
